// src/components/SelectionCountBadge/SelectionCountBadge.tsx
import React from 'react';
import { Box } from '@mui/material';
import { Check, Layers } from 'lucide-react';
import { useUiStateStore } from '@stores/uiStateStore';

/**
 * SelectionCountBadge - Shows count of selected items
 *
 * Displays a floating badge in the canvas area showing how many items
 * are currently selected. Only appears when 2+ items are selected.
 */
export const SelectionCountBadge: React.FC = () => {
  const itemControls = useUiStateStore((state) => state.itemControls);

  // Get selection count
  const selectionCount = React.useMemo(() => {
    if (itemControls?.type === 'MULTI_SELECT') {
      return itemControls.items.length;
    }
    return 0;
  }, [itemControls]);

  // Only show when multiple items are selected
  if (selectionCount < 2) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: 'rgba(59, 130, 246, 0.95)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '12px',
          boxShadow:
            '0 4px 12px rgba(59, 130, 246, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)',
          fontSize: '14px',
          fontWeight: '600',
          backdropFilter: 'blur(8px)',
          border: '2px solid rgba(255, 255, 255, 0.2)',
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        <Check size={20} />
        <span>{selectionCount} items selected</span>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginLeft: '8px',
            paddingLeft: '12px',
            borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
            fontSize: '12px',
            opacity: 0.9
          }}
        >
          <Layers size={14} />
          <span>Ctrl+G to group</span>
        </Box>
      </Box>

      <style>
        {`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
};
